<script setup lang="ts">
import type { Component } from 'vue'
import type { StatusBannerTone, StatusBannerView } from '~/features/my-wallet/types/status-banner'
import { I18nT, useI18n } from 'vue-i18n'
import IconCancel from '~icons/material-symbols/cancel-outline-rounded'
import IconCheckCircle from '~icons/material-symbols/check-circle-outline-rounded'
import IconWarning from '~icons/material-symbols/warning-outline-rounded'
import { STATUS_BANNER_PRESENTATION } from '~/features/my-wallet/utils/status-banner'

interface Props {
  banner: StatusBannerView
}

const props = defineProps<Props>()

const { t } = useI18n()

const TONE_ICON: Record<StatusBannerTone, Component> = {
  positive: IconCheckCircle,
  warning: IconWarning,
  negative: IconCancel,
}

const RINGS = [
  { radius: 311.5, strokeWidth: 8, opacity: 0.06, blur: 4 },
  { radius: 237.112, strokeWidth: 6, opacity: 0.08, blur: 3 },
  { radius: 174.032, strokeWidth: 4, opacity: 0.1, blur: 2 },
  { radius: 127.037, strokeWidth: 3, opacity: 0.12, blur: 1 },
  { radius: 84.5657, strokeWidth: 2, opacity: 0.14, blur: 0.5 },
  { radius: 44, strokeWidth: 1, opacity: 0.16, blur: 0 },
]
const RINGS_CENTER = 323.5

const filterIdPrefix = useId()

const presentation = computed(() => STATUS_BANNER_PRESENTATION[props.banner.variant])
const descriptionKey = computed(() => props.banner.isStarting && props.banner.variant === 'enabled'
  ? `${presentation.value.keyPath}.description_starting`
  : `${presentation.value.keyPath}.description`)
</script>

<template>
  <div class="wallet-status-banner" :class="`-${presentation.tone}`" role="status">
    <svg
      class="wallet-status-banner-rings"
      viewBox="0 0 647 647"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <filter
          v-for="(ring, index) in RINGS"
          :id="`${filterIdPrefix}-ring-${index}`"
          :key="index"
          x="-10%"
          y="-10%"
          width="120%"
          height="120%"
        >
          <feGaussianBlur :stdDeviation="ring.blur" />
        </filter>
      </defs>
      <circle
        v-for="(ring, index) in RINGS"
        :key="index"
        :cx="RINGS_CENTER"
        :cy="RINGS_CENTER"
        :r="ring.radius"
        stroke="currentColor"
        :stroke-width="ring.strokeWidth"
        :opacity="ring.opacity"
        :filter="ring.blur ? `url(#${filterIdPrefix}-ring-${index})` : undefined"
      />
    </svg>

    <span class="wallet-status-banner-icon" aria-hidden="true">
      <component :is="TONE_ICON[presentation.tone]" class="wallet-status-banner-icon-glyph" />
    </span>

    <div class="wallet-status-banner-text">
      <p class="wallet-status-banner-title">
        {{ t(`${presentation.keyPath}.title`) }}
      </p>
      <I18nT :keypath="descriptionKey" tag="p" class="wallet-status-banner-description" scope="global">
        <template v-if="presentation.hasHighlight" #highlight>
          <strong class="wallet-status-banner-highlight">{{ t(`${presentation.keyPath}.highlight`) }}</strong>
        </template>
        <template #contact>
          <span class="wallet-status-banner-contact">{{ t('my_wallet.status_banner.contact') }}</span>
        </template>
        <template #br>
          <br>
        </template>
      </I18nT>
    </div>
  </div>
</template>

<style scoped lang="scss">
.wallet-status-banner {
  @apply relative flex items-start gap-4 overflow-hidden p-5 rounded-lg;
  @apply bg-neutral-high-pure border border-neutral-high-medium shadow-weak;

  @screen sm:system {
    @apply items-center pt-7.5 pr-10 pb-8 pl-8;
  }

  &::before {
    @apply absolute inset-0;
    content: '';
  }

  &.-positive {
    &::before {
      @apply bg-feedback-positive-light opacity-40;
    }

    .wallet-status-banner-rings {
      @apply text-brand-secondary-pure;
    }

    .wallet-status-banner-icon {
      @apply bg-brand-secondary-pure;
    }
  }

  &.-warning {
    &::before {
      @apply bg-feedback-warning-light opacity-30;
    }

    .wallet-status-banner-rings {
      @apply text-feedback-warning-pure;
    }

    .wallet-status-banner-icon {
      @apply bg-feedback-warning-pure;
    }

    .wallet-status-banner-highlight {
      @apply text-feedback-warning-dark;
    }
  }

  &.-negative {
    &::before {
      @apply bg-feedback-negative-light opacity-25;
    }

    .wallet-status-banner-rings {
      @apply text-feedback-negative-pure;
    }

    .wallet-status-banner-icon {
      @apply bg-feedback-negative-pure;
    }

    .wallet-status-banner-highlight {
      @apply text-feedback-negative-pure;
    }
  }

  .wallet-status-banner-rings {
    @apply absolute pointer-events-none max-w-none;
    right: -196px;
    top: 53px;
    width: 631px;
    height: 631px;
    transform: translateY(-50%);
  }

  .wallet-status-banner-icon {
    @apply relative flex flex-shrink-0 items-center justify-center w-12 h-12 rounded-full;
  }

  .wallet-status-banner-icon-glyph {
    @apply w-7 h-7 text-neutral-high-pure;
  }

  .wallet-status-banner-text {
    @apply relative flex flex-col flex-1 min-w-0 gap-1;
    word-break: break-word;
  }

  .wallet-status-banner-title {
    @apply fonts-heading-h4 text-neutral-low-dark;
    line-height: 1.15;
  }

  .wallet-status-banner-description {
    @apply fonts-body-large-regular text-neutral-low-medium;
    line-height: 1.15;
  }

  .wallet-status-banner-highlight {
    @apply font-bold;
  }

  .wallet-status-banner-contact {
    @apply underline;
  }
}
</style>
